/**
 * FunChallenges/registry.ts
 *
 * Maps a `BattleType` to its UI adapter. Phase 2 sets up the indirection;
 * the actual SquareTrainerAdapter implementation lands in Phase 3, after
 * which it gets registered here. Until then, `getAdapter` returns null and
 * the BattleScreen renders a placeholder.
 */

import {BattleType} from './types';

/**
 * The minimum surface every adapter must expose.
 * Concrete shape will be locked in Phase 3 — for now it's open so we don't
 * commit to a contract before the first adapter is built.
 */
export interface BattleAdapter {
  battleType: BattleType;
  /** Render the in-battle game UI. Phase 3 adds the precise prop shape. */
  renderGame: (props: any) => React.ReactNode;
}

const adapters: Partial<Record<BattleType, BattleAdapter>> = {};

/** Register an adapter at module load time (called from Phase 3 onwards). */
export function registerAdapter(adapter: BattleAdapter): void {
  adapters[adapter.battleType] = adapter;
}

/** Returns the adapter for a given battle type, or null if none registered yet. */
export function getAdapter(battleType: BattleType): BattleAdapter | null {
  return adapters[battleType] ?? null;
}

/** All currently-registered battle types — useful for the lobby tile list. */
export function listBattleTypes(): BattleType[] {
  return Object.keys(adapters) as BattleType[];
}
